<script setup lang="ts">
/**
 * Deliberation Matrix Report (KDM report) — PROTOTYPE.
 *
 * A multi-page, print-friendly (A4) document generated from the live KDM store
 * (no extra route or controller: it reuses the already-loaded `kdm` data, exactly
 * like KnotView). Opened from the "Report" button to the right of "Print" in the
 * KnotView header.
 *
 * Pages:
 *   1. Cover            — the Deliberation Matrix name.                  (portrait)
 *   2. Table of contents — DYNAMIC Word TOC field over level 1 (the major
 *                         rubrics below) + level 2 (slice titles), with
 *                         page numbers.                                  (portrait)
 *   3. Presentation     — descriptive text + Matrix Type + URL + per-axis
 *                         value descriptions (from each value's node).  (portrait)
 *   4. Matrix slices for Typology of Object to be Compared (TOC) Axis — one
 *                         slice per Object: QPI x Stakeholders grid.      (landscape)
 *   5. Matrix slices for Quality or Performance Issue (QPI) Axis — one slice
 *                         per QPI: TOC x Stakeholders grid.               (landscape)
 *   6. Matrix slices for Stakeholder Axis — one slice per Stakeholder: QPI x
 *                         TOC grid.                                       (landscape)
 *   7. Knot View        — indicator mobilization table (the only per-indicator
 *                         detail), preceded by its sort keys.             (landscape)
 *
 * Sections 4–6 share ONE model: each axis is a level-1 heading, each of its
 * slices a level-2 heading, and every slice is a judgment grid of the two other
 * axes (NO per-indicator detail — exactly what pressing the matrix's TOC/QPI/SH
 * button shows). The Knot View carries the per-indicator detail. The dynamic TOC
 * is built from those level-1/level-2 headings.
 *
 * Print orientation (fix #1): cover, table of contents and the presentation page
 * stay PORTRAIT; the wide matrix-table pages print A4 LANDSCAPE via the named
 * `@page landscape` rule + `page: landscape` on those sections.
 *
 * Analysis-text zones persist through the existing matrix-comment system
 * (`/epbapi/comments`) under synthetic, free-form coords (see reportCoords()).
 */
import { computed, nextTick, ref, watch } from 'vue';
import { kdm, getIndicatorInfo, judgmentOptions, computeCellWinner, percentagesVisible } from '@/store/kdm';
import { buildKnotRows, type KnotRow } from '@/kdm/reportData';
import { useFetch } from '@/composables/useFetch';

const props = defineProps<{
  visible: boolean;
  groupLogo?: string;
  groupTitle?: string;
  canWrite?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

// Axis aliases for readability: TOC = columns (Object to Compare, e.g. Scénario),
// QPI = rows (Enjeux), Stakeholders = Z axis.
const tocs = computed(() => kdm.value?.columns || []);
const qpis = computed(() => kdm.value?.rows || []);
const stakeholders = computed(() => kdm.value?.stake_holders || []);

const nid = computed(() => String(kdm.value?.nid || ''));

// Workflow-state colour legend — the SAME convention the matrix uses to colour
// nodes (indicator links etc.). Sourced from the backend (single source of truth
// in DeliberationMatrixService::getModerationLegend); a small fallback mirrors
// the canonical CSS so the legend renders even on a stale payload.
const moderationLegend = computed<{ state: string; label: string; color: string; text?: string }[]>(() =>
  kdm.value?.moderation_legend && kdm.value.moderation_legend.length
    ? kdm.value.moderation_legend
    : [
        { state: 'published', label: 'Published', color: '#6c757d', text: '#6c757d' },
        { state: 'draft_unpublished', label: 'Draft (unpublished)', color: '#ffc107', text: '#997404' },
        { state: 'needs_review', label: 'Needs review', color: '#008000', text: '#008000' },
        { state: 'archived', label: 'Archived', color: '#666666', text: '#666666' },
        { state: 'deleted', label: 'Deleted (trash)', color: '#ff0000', text: '#cc0000' },
      ]
);

// The legend only explains the non-published "attention" states (Published is the
// calm baseline and is never flagged) — keeps it discreet and consistent with the
// collection report.
const nonPublishedLegend = computed(() => moderationLegend.value.filter(e => e.state !== 'published'));

const kdmTypeLabel = computed(() => {
  switch (kdm.value?.kdm_type) {
    case 1: return 'Type 1 — Standalone (direct cell values)';
    case 2: return 'Type 2 — KIK-linked (autonomous indicator selection)';
    case 3: return 'Type 3 — KRR-inherited (axes & indicators from a linked KRR)';
    default: return 'Unknown type';
  }
});

// The KDM node URL. We expose the absolute URL of the current page since the KDM
// is always rendered on its own node page; this is the canonical "Matrix URL".
const matrixUrl = computed(() => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin + window.location.pathname;
  }
  return `/node/${nid.value}`;
});

const currentDate = computed(() =>
  new Date().toLocaleDateString(document.documentElement.getAttribute('lang') || 'en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
);

// All flat indicator/judgment rows (one per evaluated indicator per cell). Shared
// with the Knot View page and reused to slice by axis crossing on pages 5/6/7.
const knotRows = computed<KnotRow[]>(() => buildKnotRows());

// Knot View ordering: mirror the Knot View's default sort (Indicator A → Z),
// with stable tie-breakers across the other axes. The keys are printed above the
// Knot View table so the reader knows how the rows are ordered.
const KNOT_SORT_NOTE =
  'Sorted by: Indicator (A → Z), then Stakeholder, then Typology of Object to be Compared (TOC), then Quality or Performance Issue (QPI).';
const sortedKnotRows = computed<KnotRow[]>(() =>
  [...knotRows.value].sort((a, b) =>
    a.iprTitle.localeCompare(b.iprTitle)
    || a.stakeholderTitle.localeCompare(b.stakeholderTitle)
    || a.tocTitle.localeCompare(b.tocTitle)
    || a.qpiTitle.localeCompare(b.qpiTitle)
  )
);

// ---- Workflow-state display. ----
// The report ALWAYS shows workflow-state tints + legend, exactly like the live
// matrix (KDMTable tints every title by moderation_class). There is deliberately
// no "Only published content" toggle: a deliberation matrix never filters by
// moderation state — that would falsify the aggregates — so the report must show
// the SAME content and the SAME state markers as the online version.
const showStates = ref(true);

// State machine name -> readable text colour (from the backend legend).
const stateTextColor = computed<Record<string, string>>(() => {
  const m: Record<string, string> = {};
  for (const e of moderationLegend.value) m[e.state] = (e as any).text || e.color;
  return m;
});
const classToState = (modClass?: string): string => (modClass || '').replace(/^moderation_state-/, '');
const isFlaggable = (state: string): boolean => state !== '' && state !== 'published' && state !== 'unknown';

// Inline style tinting a title by its workflow state (only when states shown).
function tintStyle(modClass?: string): Record<string, string> {
  if (!showStates.value) return {};
  const st = classToState(modClass);
  const c = isFlaggable(st) ? stateTextColor.value[st] : '';
  return c ? { color: c } : {};
}
// Hex colour (no '#') for the Word model, or '' when no tint applies.
function tintHex(modClass?: string): string {
  if (!showStates.value) return '';
  const st = classToState(modClass);
  return isFlaggable(st) ? (stateTextColor.value[st] || '').replace('#', '') : '';
}
// Distinct flaggable states actually present (axes + mobilised IPRs).
const presentStates = computed<string[]>(() => {
  const set = new Set<string>();
  const add = (mc?: string) => { const s = classToState(mc); if (isFlaggable(s)) set.add(s); };
  for (const it of [...tocs.value, ...qpis.value, ...stakeholders.value] as any[]) add(it.moderation_class);
  for (const r of knotRows.value) add(getIndicatorInfo(r.iprId).moderation_class);
  return [...set];
});
// Legend entries: present non-published states, only when states are shown.
const legendEntries = computed(() => showStates.value
  ? nonPublishedLegend.value.filter(e => presentStates.value.includes(e.state))
  : []);

// ---- Axis value descriptions (fix #7). ----
// Each axis item carries `description`: the truncated text of its node's first
// "Text - Image" (epb_txt_img) paragraph, resolved server-side in KdmController.
function axisDescription(item: any): string {
  return (item && typeof item.description === 'string') ? item.description : '';
}

// ---- Slice-grid cell lookup: the rows at one (Stakeholder, QPI, TOC) crossing. ----
function cellRowsFor(shId: string, qpiId: string, tocId: string): KnotRow[] {
  return knotRows.value.filter(r => r.stakeholderId === shId && r.qpiId === qpiId && r.tocId === tocId);
}

// Pick a readable text color for a judgment background (mirrors KnotView logic).
function readableTextColor(bg?: string): string {
  if (!bg) return '#212529';
  const s = bg.trim();
  let r = 0, g = 0, b = 0;
  const m6 = /^#([0-9a-f]{6})$/i.exec(s);
  const m3 = /^#([0-9a-f]{3})$/i.exec(s);
  if (m6) { r = parseInt(s.slice(1, 3), 16); g = parseInt(s.slice(3, 5), 16); b = parseInt(s.slice(5, 7), 16); }
  else if (m3) { r = parseInt(s[1] + s[1], 16); g = parseInt(s[2] + s[2], 16); b = parseInt(s[3] + s[3], 16); }
  else return '#212529';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // 6-char hex: Word ignores 3-char colours (white rendered as default blue).
  return luminance > 0.6 ? '#212529' : '#ffffff';
}

function isOrphanIpr(iprId: string): boolean {
  return getIndicatorInfo(iprId).is_in_kik === false;
}

// ===================== EXPORT: real .docx via PhpWord (server-side) ============
// The client assembles a portable report MODEL (sections with an orientation +
// blocks: image / heading / paragraph / blank-space / table) and POSTs it; the
// server renders it with PhpWord so the matrix sections are REAL A4 landscape
// pages and the document opens natively in LibreOffice / Word. Analysis areas
// are three blank lines (no comment-backed storage).

// A judgment cell for the slice grid: the SAME aggregation the matrix uses
// (computeCellWinner → the winning judgment + its proportional weight), so the
// report mirrors the matrix exactly instead of splitting per indicator.
//   { s: winner label, bg: hex (no #), color: readable text hex, pct: 0–100 }
function judgmentCell(rows: KnotRow[]): any {
  if (!rows.length) return { s: '—', color: 'ADB5BD', pct: 0 };
  // Rebuild the raw cell data this crossing holds, then run the shared winner
  // algorithm (KDM type 1 = direct value; types 2/3 = aggregate of indicators).
  const kdmType = kdm.value?.kdm_type;
  let cellData: any;
  if (kdmType === 1) {
    cellData = { judgmentId: rows[0].evaluationId, sliderValue: rows[0].sliderValue };
  }
  else {
    cellData = {};
    for (const r of rows) {
      cellData[r.iprId] = { judgmentId: r.evaluationId, sliderValue: r.sliderValue };
    }
  }
  const winner = computeCellWinner(cellData, kdmType);
  if (!winner) {
    const first = rows[0];
    return {
      s: first.evaluationLabel || '—',
      bg: (first.evaluationColor || '').replace('#', ''),
      color: readableTextColor(first.evaluationColor).replace('#', ''),
      pct: Math.round(first.sliderValue || 0),
    };
  }
  const label = judgmentOptions.value.get(winner.judgmentId)?.title || '';
  return {
    s: label || '—',
    bg: (winner.color || '').replace('#', ''),
    color: readableTextColor(winner.color).replace('#', ''),
    pct: Math.max(0, Math.min(100, Math.round(winner.totalWeight || 0))),
    // The bar is always drawn; the % NUMBER follows the matrix "%" toggle
    // (generally ON for KDM type 2/3, OFF for type 1) — same as SummaryView.
    showPct: percentagesVisible.value,
  };
}

// Consolidated cell summary for the HTML grids (or null when the crossing is
// empty) — same object as the Word model, so HTML and Word match.
function cellSummary(shId: string, qpiId: string, tocId: string): any {
  const rows = cellRowsFor(shId, qpiId, tocId);
  return rows.length ? judgmentCell(rows) : null;
}
// Proportional-bar style mirroring the matrix SummaryView: the winner colour
// fills `pct%` of the cell, the rest is transparent.
function barStyle(sum: any): Record<string, string> {
  const color = '#' + (sum?.bg || 'ADB5BD');
  return { background: `linear-gradient(to right, ${color} ${sum?.pct || 0}%, transparent ${sum?.pct || 0}%)` };
}
// Hover tooltip: the per-indicator detail behind the aggregated cell.
function cellTitle(shId: string, qpiId: string, tocId: string): string {
  return cellRowsFor(shId, qpiId, tocId)
    .map(r => `${r.iprTitle}: ${r.evaluationLabel} (${r.sliderValue}%)`)
    .join('\n');
}

// ---- Comments inclusion (toggle): print the threads like Knot View. ----
const ofetch = useFetch();
const includeComments = ref(false);
const commentsLoading = ref(false);
const commentThreads = ref<Record<string, any[]>>({});

// ---- Essential dependencies (Worksite / KIK / KRR) shown in the "Matrix type"
// box (type 1 → Worksite; 2 → +KIK; 3 → +KRR). ----
const dependencies = computed<{ role: string; id: number; title: string; href: string }[]>(
  () => (kdm.value as any)?.dependencies || []
);

// ---- "All fields" toggle (cumulative): add an "Axis values" chapter rendering
// every axis value + IPR with its FULL field set. The full-field HTML is costly,
// so it is fetched on demand (?full_fields=1) only when the toggle is turned on. ----
const allFields = ref(false);
const fullFieldsLoading = ref(false);
const fullHtmlMap = ref<Record<string, string>>({});
async function loadFullFields() {
  if (Object.keys(fullHtmlMap.value).length) return;
  fullFieldsLoading.value = true;
  try {
    const res: any = await ofetch(`/epbapi/kdm/${nid.value}?full_fields=1`);
    const map: Record<string, string> = {};
    for (const key of ['columns', 'rows', 'stake_holders']) {
      for (const it of (res?.[key] || [])) {
        if (it?.full_html) map[String(it.id)] = it.full_html;
      }
    }
    for (const it of (res?.iprs || [])) {
      if (it?.full_html) map[String(it.id)] = it.full_html;
    }
    fullHtmlMap.value = map;
  } catch { /* keep empty — the chapter just shows nothing */ }
  finally { fullFieldsLoading.value = false; }
}
watch(allFields, (on) => { if (on) loadFullFields(); });
// Axis values (+ IPRs) that actually have a full-field render, for the chapter.
const allFieldsEntries = computed(() => {
  const out: { id: string; title: string; group: string; html: string }[] = [];
  const push = (group: string, items: any[]) => {
    for (const it of items) {
      const html = fullHtmlMap.value[String(it.id)];
      if (html) out.push({ id: String(it.id), title: it.title, group, html });
    }
  };
  push('Typology of Object to be Compared (TOC)', tocs.value as any[]);
  push('Quality or Performance Issue (QPI)', qpis.value as any[]);
  push('Stakeholders', stakeholders.value as any[]);
  push('Indicators (IPR)', (kdm.value as any)?.iprs || []);
  return out;
});

function stripHtml(html: string): string {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// Fetch the full comment thread of every row that has comments (reuses the same
// endpoint as the Knot View / discussion panel), deduped by coords.
// A reserved key for the matrix-level (general) comment thread — fetched with no
// coords param so it covers field_matrix_coords IN (node id, NULL).
const MATRIX_COORDS = '__matrix__';
async function loadComments() {
  // Every coords that actually carries comments (cell SH-QPI-TOC AND indicator
  // SH-QPI-TOC-ipr), read from the server-built comment_summary so we also catch
  // cell-level comments not tied to a Knot row.
  const summary = (kdm.value as any)?.comment_summary || {};
  const coordsList = Object.keys(summary)
    .filter(c => (summary[c]?.count || 0) > 0 && c !== String(nid.value) && !(c in commentThreads.value));
  const needGeneral = !(MATRIX_COORDS in commentThreads.value);
  if (!coordsList.length && !needGeneral) return;
  commentsLoading.value = true;
  try {
    const jobs: Promise<void>[] = coordsList.map(async (coords) => {
      try {
        const res: any = await ofetch(`/epbapi/comments/${nid.value}?coords=${encodeURIComponent(coords)}`);
        commentThreads.value = { ...commentThreads.value, [coords]: Array.isArray(res) ? res : (res?.comments ?? []) };
      } catch {
        commentThreads.value = { ...commentThreads.value, [coords]: [] };
      }
    });
    if (needGeneral) {
      jobs.push((async () => {
        try {
          const res: any = await ofetch(`/epbapi/comments/${nid.value}`);
          commentThreads.value = { ...commentThreads.value, [MATRIX_COORDS]: Array.isArray(res) ? res : (res?.comments ?? []) };
        } catch {
          commentThreads.value = { ...commentThreads.value, [MATRIX_COORDS]: [] };
        }
      })());
    }
    await Promise.all(jobs);
  } finally {
    commentsLoading.value = false;
  }
}
watch(includeComments, (on) => { if (on) loadComments(); });

// Estimated page count, shown next to the Download button (parity with the
// Collection report). The report is laid out as discrete A4 .dm-report__page
// sections, so we count the rendered pages. Re-measured when the report opens
// and whenever the opt-in chapters (comments / full fields) finish loading.
const pageCount = ref(0);
function measurePages() {
  pageCount.value = document.querySelectorAll('.dm-report .dm-report__page').length;
}
watch([() => props.visible, fullFieldsLoading, commentsLoading], () => {
  if (props.visible && !fullFieldsLoading.value && !commentsLoading.value) {
    nextTick(measurePages);
  }
}, { immediate: true });

// A flat "Author: body" rendering of a coords thread (for tables / the .docx).
function commentText(coords: string): string {
  const out: string[] = [];
  const walk = (arr: any[]) => (arr || []).forEach((c: any) => {
    out.push(`${c.author}: ${stripHtml(c.body)}`);
    if (c.children?.length) walk(c.children);
  });
  walk(commentThreads.value[coords] || []);
  return out.join('\n');
}

// The matrix-level (general) comment thread, flattened — printed at the end of
// the Presentation section when "Include comments" is on.
const generalCommentText = computed(() => commentText(MATRIX_COORDS));

// All comments for one (Stakeholder, QPI, TOC) cell: the cell-level thread plus
// each mobilised indicator's thread (prefixed by the indicator title). Used by
// the per-cell comment grids that mirror the judgment grids.
function cellCommentText(shId: string, qpiId: string, tocId: string): string {
  const parts: string[] = [];
  const cell = commentText(`${shId}-${qpiId}-${tocId}`);
  if (cell) parts.push(cell);
  for (const r of cellRowsFor(shId, qpiId, tocId)) {
    if (r.indicatorCoords === `${shId}-${qpiId}-${tocId}`) continue;
    const t = commentText(r.indicatorCoords);
    if (t) parts.push(`[${r.iprTitle}] ${t}`);
  }
  return parts.join('\n');
}

// The slice-level discussion (coords slice-{axis}-{id}), flattened. A "slice"
// comment is left on a whole presented slice via the matrix's SH/TOC/QPI view —
// distinct from the per-cell and per-indicator threads above.
function sliceCommentText(axis: 'toc' | 'qpi' | 'stakeholder', id: string): string {
  return commentText(`slice-${axis}-${id}`);
}

// Does a TOC slice carry ANY cell comment? (gate the per-slice comment grids so
// slices without comments don't sprout empty tables).
function sliceHasComments(tocId: string): boolean {
  for (const sh of stakeholders.value as any[]) {
    for (const qpi of qpis.value as any[]) {
      if (cellCommentText(sh.id, qpi.id, tocId)) return true;
    }
  }
  return false;
}
// Above 5 stakeholders a combined comment grid is unreadable (columns too narrow)
// → render one table per stakeholder instead.
const COMMENT_GRID_MAX_SH = 5;
const commentGridSplit = computed(() => (stakeholders.value as any[]).length > COMMENT_GRID_MAX_SH);

// In-report TOC navigation: scroll the report overlay to a section/slice anchor
// (matches the collection report's clickable TOC).
function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
// Print / Save as PDF (the report overlay carries its own print CSS that hides
// the rest of the page), matching the collection report's Print button.
function printReport() {
  window.print();
}

// The Comments cell of a data row: the thread text when "Include comments" is on
// and loaded, otherwise the count.
function commentCell(row: KnotRow): any {
  if (includeComments.value) {
    const t = commentText(row.indicatorCoords);
    if (t) return { s: t };
  }
  return { s: row.commentCount > 0 ? String(row.commentCount) : '—' };
}

// Canonical axis names (replace the legacy "Object to Compare" / "Enjeu").
const TOC_AXIS = 'Typology of Object to be Compared';
const QPI_AXIS = 'Quality or Performance Issue';

function buildReportModel(): any {
  const title = kdm.value?.title || 'Deliberation Matrix Report';

  // 1. Cover (portrait) — centered logo + title. The cover title stays an H1
  //    but the table of contents is built only from the content sections below.
  const cover: any[] = [];
  if (props.groupLogo) cover.push({ t: 'image', src: props.groupLogo, width: 150, align: 'center' });
  if (props.groupTitle) cover.push({ t: 'p', s: props.groupTitle, align: 'center' });
  cover.push({ t: 'p', s: 'Deliberation Matrix Report', align: 'center' });
  // The cover title is a large paragraph (NOT a heading) so it is excluded from
  // the Table of Contents.
  cover.push({ t: 'p', s: title, bold: true, size: 22, align: 'center' });
  cover.push({ t: 'p', s: currentDate.value, align: 'center' });

  // Workflow-state colour legend on the cover (page 1): a discreet inline line of
  // coloured dots + labels, rendered by the shared DocxReportService 'legend'
  // block — only the present non-published states, and only when states are shown.
  if (legendEntries.value.length) {
    cover.push({ t: 'space' });
    cover.push({
      t: 'legend',
      title: 'Workflow states',
      entries: legendEntries.value.map(e => ({ color: e.color.replace('#', ''), label: e.label })),
    });
  }

  // Content sections are assembled FIRST so the table of contents can be derived
  // from their real H1 (major rubric) / H2 (sub-rubric) headings.
  const content: any[] = [];

  // A. Presentation (portrait): meta table, descriptive space, axis descriptions.
  const pres: any[] = [
    { t: 'h', l: 1, s: 'Presentation of the Deliberation Matrix' },
    { t: 'table', head: [], rows: [
      [{ s: 'Matrix name', bold: true }, { s: title }],
      [{ s: 'Matrix type', bold: true }, { s: kdmTypeLabel.value }],
      ...dependencies.value.map(d => ([{ s: d.role, bold: true }, { s: d.title }])),
      [{ s: 'Matrix URL', bold: true }, { s: matrixUrl.value }],
    ] },
    { t: 'h', l: 3, s: 'Descriptive text' },
    { t: 'space' },
    { t: 'h', l: 3, s: 'Axis values — descriptions' },
  ];
  const axisGroup = (label: string, items: any[]) => {
    pres.push({ t: 'p', s: label, bold: true });
    for (const it of items) {
      pres.push({ t: 'p', s: it.title, bold: true });
      // axisDescription is COLLECTED content — left verbatim.
      pres.push({ t: 'p', s: axisDescription(it) || 'No description.' });
    }
  };
  axisGroup(`${TOC_AXIS} (TOC)`, tocs.value as any[]);
  axisGroup(`${QPI_AXIS} (QPI)`, qpis.value as any[]);
  axisGroup('Stakeholders', stakeholders.value as any[]);
  // General (matrix-level) comments at the end of the Presentation section.
  if (includeComments.value && generalCommentText.value) {
    pres.push({ t: 'h', l: 3, s: 'General comments' });
    pres.push({ t: 'p', s: generalCommentText.value });
  }
  content.push({ orientation: 'portrait', blocks: pres });

  // B. Matrix slices for the TOC axis (landscape): the per-Object grids
  //    (QPI × Stakeholders), one per Object to Compare.
  const tocAxis: any[] = [
    { t: 'h', l: 1, s: `Matrix slices for ${TOC_AXIS} (TOC) Axis` },
    { t: 'p', s: `Each ${TOC_AXIS} (e.g. a scenario) is a slice of the deliberation cube: the ${QPI_AXIS} (QPI) crossed with the Stakeholders.` },
  ];
  for (const toc of tocs.value as any[]) {
    tocAxis.push({ t: 'h', l: 2, s: toc.title, color: tintHex(toc.moderation_class) });
    const d = axisDescription(toc); if (d) tocAxis.push({ t: 'p', s: d });
    const tocSlice = includeComments.value ? sliceCommentText('toc', toc.id) : '';
    if (tocSlice) { tocAxis.push({ t: 'p', s: 'Slice discussion', bold: true }); tocAxis.push({ t: 'p', s: tocSlice }); }
    const head = [`${QPI_AXIS} \\ Stakeholder`, ...(stakeholders.value as any[]).map(s => s.title)];
    const rows = (qpis.value as any[]).map(qpi => ([
      { s: qpi.title, bold: true },
      ...(stakeholders.value as any[]).map(sh => judgmentCell(cellRowsFor(sh.id, qpi.id, toc.id))),
    ]));
    tocAxis.push({ t: 'table', head, rows });
    // Per-cell COMMENTS grid mirroring the judgment grid (Include comments on).
    if (includeComments.value && sliceHasComments(toc.id)) {
      tocAxis.push({ t: 'p', s: 'Comments', bold: true });
      if (!commentGridSplit.value) {
        const chead = [`${QPI_AXIS} \\ Stakeholder`, ...(stakeholders.value as any[]).map(s => s.title)];
        const crows = (qpis.value as any[]).map(qpi => ([
          { s: qpi.title, bold: true },
          ...(stakeholders.value as any[]).map(sh => ({ s: cellCommentText(sh.id, qpi.id, toc.id) || '—' })),
        ]));
        tocAxis.push({ t: 'table', head: chead, rows: crows });
      }
      else {
        // >5 stakeholders: one table per stakeholder (combined columns too narrow).
        for (const sh of stakeholders.value as any[]) {
          tocAxis.push({ t: 'p', s: sh.title, bold: true });
          const crows = (qpis.value as any[]).map(qpi => ([
            { s: qpi.title, bold: true },
            { s: cellCommentText(sh.id, qpi.id, toc.id) || '—' },
          ]));
          tocAxis.push({ t: 'table', head: [], rows: crows });
        }
      }
    }
    tocAxis.push({ t: 'space' });
  }
  content.push({ orientation: 'landscape', blocks: tocAxis });

  // C. Matrix slices for the QPI axis (landscape): one slice per Quality or
  //    Performance Issue — the same model as the TOC axis (NO per-indicator
  //    detail). Each slice is the TOC (rows) crossed with the Stakeholders
  //    (cols), as when the matrix's "QPI" button is pressed.
  const qpiAxis: any[] = [
    { t: 'h', l: 1, s: `Matrix slices for ${QPI_AXIS} (QPI) Axis` },
    { t: 'p', s: `Each ${QPI_AXIS} (QPI) is a slice of the deliberation cube: the ${TOC_AXIS} (TOC) crossed with the Stakeholders.` },
  ];
  for (const qpi of qpis.value as any[]) {
    qpiAxis.push({ t: 'h', l: 2, s: qpi.title, color: tintHex(qpi.moderation_class) });
    const d = axisDescription(qpi); if (d) qpiAxis.push({ t: 'p', s: d });
    const qpiSlice = includeComments.value ? sliceCommentText('qpi', qpi.id) : '';
    if (qpiSlice) { qpiAxis.push({ t: 'p', s: 'Slice discussion', bold: true }); qpiAxis.push({ t: 'p', s: qpiSlice }); }
    const head = [`${TOC_AXIS} \\ Stakeholder`, ...(stakeholders.value as any[]).map(s => s.title)];
    const rows = (tocs.value as any[]).map(toc => ([
      { s: toc.title, bold: true },
      ...(stakeholders.value as any[]).map(sh => judgmentCell(cellRowsFor(sh.id, qpi.id, toc.id))),
    ]));
    qpiAxis.push({ t: 'table', head, rows });
    qpiAxis.push({ t: 'space' });
  }
  content.push({ orientation: 'landscape', blocks: qpiAxis });

  // D. Matrix slices for the Stakeholder axis (landscape): one slice per
  //    Stakeholder — same model again (NO per-indicator detail). Each slice is
  //    the QPI (rows) crossed with the TOC (cols), as when the matrix's "SH"
  //    button is pressed.
  const shAxis: any[] = [
    { t: 'h', l: 1, s: 'Matrix slices for Stakeholder Axis' },
    { t: 'p', s: `Each Stakeholder is a slice of the deliberation cube: the ${QPI_AXIS} (QPI) crossed with the ${TOC_AXIS} (TOC).` },
  ];
  for (const sh of stakeholders.value as any[]) {
    shAxis.push({ t: 'h', l: 2, s: sh.title, color: tintHex(sh.moderation_class) });
    const d = axisDescription(sh); if (d) shAxis.push({ t: 'p', s: d });
    const shSlice = includeComments.value ? sliceCommentText('stakeholder', sh.id) : '';
    if (shSlice) { shAxis.push({ t: 'p', s: 'Slice discussion', bold: true }); shAxis.push({ t: 'p', s: shSlice }); }
    const head = [`${QPI_AXIS} \\ ${TOC_AXIS}`, ...(tocs.value as any[]).map(t => t.title)];
    const rows = (qpis.value as any[]).map(qpi => ([
      { s: qpi.title, bold: true },
      ...(tocs.value as any[]).map(toc => judgmentCell(cellRowsFor(sh.id, qpi.id, toc.id))),
    ]));
    shAxis.push({ t: 'table', head, rows });
    shAxis.push({ t: 'space' });
  }
  content.push({ orientation: 'landscape', blocks: shAxis });

  // E. Knot View (landscape): the only per-indicator detail. Rows are sorted to
  //    mirror the Knot View default order; the sort keys are printed above the
  //    table.
  const knotRowsData = sortedKnotRows.value.map(r => ([
    // Indicator title, tinted by its workflow state in all-states mode.
    { s: r.iprTitle, ...(tintHex(getIndicatorInfo(r.iprId).moderation_class) ? { color: tintHex(getIndicatorInfo(r.iprId).moderation_class) } : {}) },
    { s: r.evaluationLabel || '—', bg: (r.evaluationColor || '').replace('#', ''), color: readableTextColor(r.evaluationColor).replace('#', '') },
    { s: `${r.sliderValue}%` },
    { s: r.tocTitle },
    { s: r.qpiTitle },
    { s: r.stakeholderTitle },
    commentCell(r),
  ]));
  content.push({ orientation: 'landscape', blocks: [
    { t: 'h', l: 1, s: 'Knot View — indicator mobilization' },
    { t: 'p', s: KNOT_SORT_NOTE, italic: true },
    { t: 'table', head: ['Indicator', 'Judgment', 'Weight', TOC_AXIS, QPI_AXIS, 'Stakeholder', 'Comments'], rows: knotRowsData },
  ] });

  // F. Axis values — full fields chapter ("All fields" toggle): each axis value
  //    + IPR with its full field render as a rich-text ('html') block.
  if (allFields.value && allFieldsEntries.value.length) {
    const axisFull: any[] = [{ t: 'h', l: 1, s: 'Axis values — full fields' }];
    for (const entry of allFieldsEntries.value) {
      axisFull.push({ t: 'h', l: 2, s: entry.title });
      axisFull.push({ t: 'p', s: entry.group, italic: true });
      axisFull.push({ t: 'html', s: entry.html });
    }
    content.push({ orientation: 'portrait', blocks: axisFull });
  }

  // 2. Dynamic Table of Contents (portrait): a real Word TOC field over levels
  //    1 (major rubrics, defined) and 2 (slice titles), WITH page numbers. The
  //    "Table of contents" caption is a plain paragraph (not a heading) so it
  //    doesn't list itself.
  const tocSection = {
    orientation: 'portrait',
    blocks: [
      { t: 'p', s: 'Table of contents', bold: true, size: 15 },
      { t: 'toc', min: 1, max: 2 },
    ],
  };

  const sections: any[] = [
    { orientation: 'portrait', blocks: cover },
    tocSection,
    ...content,
  ];

  const filename = (title || 'deliberation_matrix_report').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'report';
  return { filename, sections };
}

const exporting = ref(false);
// When ON, generate inside the branded Word template via the docgen microservice
// (same model, different endpoint); OFF = the plain PhpWord layout.
const useTemplate = ref(false);
// The "Branded template" toggle is offered only when the node's group has an
// uploaded branded Word template.
const hasTemplate = ref(false);
watch(nid, async (v) => {
  if (!v) { hasTemplate.value = false; return; }
  try {
    const r: any = await ofetch(`/epbapi/docgen/${v}/has-template`);
    hasTemplate.value = !!(r && r.has);
  }
  catch {
    hasTemplate.value = false;
  }
}, { immediate: true });
// User-facing export error (e.g. the branded document service is unavailable).
const exportError = ref('');
async function downloadDocx() {
  if (exporting.value) return;
  exporting.value = true;
  exportError.value = '';
  try {
    const endpoint = useTemplate.value
      ? `/epb/docgen/report/${nid.value}`
      : `/epbapi/kdm/${nid.value}/report-docx`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildReportModel()),
    });
    if (!res.ok) throw new Error('Export failed: ' + res.status);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(kdm.value?.title || 'deliberation_matrix_report').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'report'}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    console.error('[Report] docx export failed', e);
    exportError.value = useTemplate.value
      ? 'Branded document generation failed — the document service may be unavailable. Try again, or untick “Branded template”.'
      : 'Document export failed. Please try again.';
  } finally {
    exporting.value = false;
  }
}
</script>

<template>
  <div v-if="visible" class="dm-report">
    <!-- Screen-only toolbar (hidden when printing) -->
    <div class="dm-report__toolbar">
      <div class="dm-report__toolbar-title">
        <i class="bi bi-file-earmark-text me-2"></i>
        Deliberation Matrix Report
      </div>
      <div class="dm-report__toolbar-actions">
        <label class="dm-report__toggle" title="Include the full comment threads (as in the Knot View) in the report and the export">
          <input type="checkbox" v-model="includeComments" />
          Include comments
          <span v-if="commentsLoading" class="spinner-border spinner-border-sm ms-1" role="status"></span>
        </label>
        <label class="dm-report__toggle" title="Add an Axis values chapter listing every axis value + indicator with its full field set">
          <input type="checkbox" v-model="allFields" />
          All fields
          <span v-if="fullFieldsLoading" class="spinner-border spinner-border-sm ms-1" role="status"></span>
        </label>
        <button type="button" class="btn btn-sm btn-outline-primary" :disabled="exporting" @click="downloadDocx" title="Download as a Word document (.docx) — opens in LibreOffice / Word">
          <span v-if="exporting" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="bi bi-file-earmark-word me-1"></i>
          Download (.docx)<span v-if="pageCount" class="dm-report__pages-count" title="Estimated page count (A4)"> — ~{{ pageCount }} p.</span>
        </button>
        <label v-if="hasTemplate" class="dm-report__toggle" title="Applies to the Word (.docx) download ONLY: generates inside the group's branded Word template (cover, header/footer, logos). The on-screen report and the PDF are not affected (a Word template cannot brand HTML/PDF).">
          <input type="checkbox" v-model="useTemplate" />
          Branded template
        </label>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('close')" title="Close report">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div v-if="exportError" class="alert alert-danger py-1 px-2 mb-0 mt-1 small w-100" role="alert">
        <i class="bi bi-exclamation-triangle me-1"></i>{{ exportError }}
      </div>
      <!-- Branded-export warning: shown (always open, not collapsible) only when
           the Branded template toggle is ON. Purely informational. -->
      <div v-if="useTemplate" class="dm-report__branding-note small mt-1" role="note">
        <div class="dm-report__branding-note-title"><i class="bi bi-info-circle me-1"></i>About the branded Word export — what is kept and what is removed</div>
        <div class="dm-report__branding-note-body">
          With <strong>Branded template</strong> on, the Word (.docx) download is generated <em>inside</em> your group's branded template:
          <ul class="mb-1">
            <li>The cover, header, footer and logos are kept; the report body, title and acronyms are inserted at the template's markers.</li>
            <li>Headings take the template's styles and its Table of Contents rebuilds when you open the file in Word.</li>
            <li>Sections the template marks as not auto-fillable (e.g. annexes, references, style demos) are <strong>removed</strong> from the export; placeholder pages you fill by hand (executive summary, document information…) stay as in the template.</li>
            <li>This affects the Word download <strong>only</strong> — the on-screen report and the PDF are not branded (a Word template cannot brand HTML/PDF).</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="dm-report__pages">
      <!-- ============ Cover (portrait) ============ -->
      <section class="dm-report__page dm-report__page--portrait dm-report__page--cover">
        <div class="dm-report__cover-inner">
          <img v-if="groupLogo" :src="groupLogo" alt="Group logo" class="dm-report__cover-logo" />
          <div v-if="groupTitle" class="dm-report__cover-group">{{ groupTitle }}</div>
          <div class="dm-report__cover-kicker">Deliberation Matrix Report</div>
          <h1 class="dm-report__cover-title">{{ kdm?.title }}</h1>
          <div class="dm-report__cover-date">{{ currentDate }}</div>

          <!-- Workflow-state colour legend: discreet single line, only the
               non-published states actually present, and only when "Only
               published content" is OFF. Same convention as the collection report. -->
          <div v-if="legendEntries.length" class="dm-report__legend">
            <span class="dm-report__legend-title">Workflow states:</span>
            <span v-for="entry in legendEntries" :key="entry.state" class="dm-report__legend-item">
              <span class="dm-report__legend-dot" :style="{ background: entry.color }"></span>
              {{ entry.label }}
            </span>
          </div>
        </div>
        <div class="dm-report__page-footer">Cover</div>
      </section>

      <!-- ============ Table of contents (portrait) — fix #2 ============ -->
      <section class="dm-report__page dm-report__page--portrait">
        <h2 class="dm-report__page-title">Table of contents</h2>
        <ol class="dm-report__toc">
          <li><a href="#" @click.prevent="scrollToSection('dm-sec-presentation')">Presentation of the Deliberation Matrix</a></li>
          <li>
            <a href="#" @click.prevent="scrollToSection('dm-sec-toc')">Matrix slices for Typology of Object to be Compared (TOC) Axis</a>
            <ul class="dm-report__toc-sub">
              <li v-for="toc in tocs" :key="toc.id"><a href="#" @click.prevent="scrollToSection('dm-toc-' + toc.id)">{{ toc.title }}</a></li>
            </ul>
          </li>
          <li>
            <a href="#" @click.prevent="scrollToSection('dm-sec-qpi')">Matrix slices for Quality or Performance Issue (QPI) Axis</a>
            <ul class="dm-report__toc-sub">
              <li v-for="qpi in qpis" :key="qpi.id"><a href="#" @click.prevent="scrollToSection('dm-qpi-' + qpi.id)">{{ qpi.title }}</a></li>
            </ul>
          </li>
          <li>
            <a href="#" @click.prevent="scrollToSection('dm-sec-sh')">Matrix slices for Stakeholder Axis</a>
            <ul class="dm-report__toc-sub">
              <li v-for="sh in stakeholders" :key="sh.id"><a href="#" @click.prevent="scrollToSection('dm-sh-' + sh.id)">{{ sh.title }}</a></li>
            </ul>
          </li>
          <li><a href="#" @click.prevent="scrollToSection('dm-sec-knot')">Knot View — indicator mobilization</a></li>
        </ol>
        <p class="dm-report__toc-note">
          One level-1 heading per axis, one level-2 heading per slice. The exported
          .docx builds a real Word table of contents with page numbers from these.
          The matrix-table sections print in A4 landscape; the cover, this contents
          page and the presentation print in portrait.
        </p>
        <div class="dm-report__page-footer">Contents</div>
      </section>

      <!-- ============ Presentation (portrait) ============ -->
      <section class="dm-report__page dm-report__page--portrait">
        <h2 id="dm-sec-presentation" class="dm-report__page-title">Presentation of the Deliberation Matrix</h2>

        <div class="dm-report__meta">
          <div class="dm-report__meta-row">
            <span class="dm-report__meta-label">Matrix name</span>
            <span class="dm-report__meta-value">{{ kdm?.title }}</span>
          </div>
          <div class="dm-report__meta-row">
            <span class="dm-report__meta-label">Matrix type</span>
            <span class="dm-report__meta-value">
              {{ kdmTypeLabel }}
              <ul v-if="dependencies.length" class="dm-report__deps">
                <li v-for="d in dependencies" :key="d.id">
                  <strong>{{ d.role }}:</strong>
                  <a :href="d.href">{{ d.title }}</a>
                </li>
              </ul>
            </span>
          </div>
          <div class="dm-report__meta-row">
            <span class="dm-report__meta-label">Matrix URL</span>
            <a class="dm-report__meta-value dm-report__meta-link" :href="matrixUrl">{{ matrixUrl }}</a>
          </div>
        </div>

        <h3 class="dm-report__section-title">Descriptive text</h3>
        <div class="dm-report__analysis-space"><strong>Description</strong> — three blank lines in the exported document for handwriting/typing.</div>

        <!-- Axis value descriptions, pulled from each value's node (fix #7). -->
        <template v-if="tocs.length || qpis.length || stakeholders.length">
          <h3 class="dm-report__section-title">Axis values — descriptions</h3>

          <template v-if="tocs.length">
            <h4 class="dm-report__subsection-title">Typology of Object to be Compared (TOC)</h4>
            <dl class="dm-report__desc-list">
              <template v-for="toc in tocs" :key="toc.id">
                <dt>{{ toc.title }}</dt>
                <dd v-if="axisDescription(toc)">{{ axisDescription(toc) }}</dd>
                <dd v-else class="dm-report__desc-empty">No description.</dd>
              </template>
            </dl>
          </template>

          <template v-if="qpis.length">
            <h4 class="dm-report__subsection-title">Quality or Performance Issue (QPI)</h4>
            <dl class="dm-report__desc-list">
              <template v-for="qpi in qpis" :key="qpi.id">
                <dt>{{ qpi.title }}</dt>
                <dd v-if="axisDescription(qpi)">{{ axisDescription(qpi) }}</dd>
                <dd v-else class="dm-report__desc-empty">No description.</dd>
              </template>
            </dl>
          </template>

          <template v-if="stakeholders.length">
            <h4 class="dm-report__subsection-title">Stakeholders</h4>
            <dl class="dm-report__desc-list">
              <template v-for="sh in stakeholders" :key="sh.id">
                <dt>{{ sh.title }}</dt>
                <dd v-if="axisDescription(sh)">{{ axisDescription(sh) }}</dd>
                <dd v-else class="dm-report__desc-empty">No description.</dd>
              </template>
            </dl>
          </template>
        </template>

        <!-- General (matrix-level) comments — only when "Include comments" is on. -->
        <template v-if="includeComments && generalCommentText">
          <h3 class="dm-report__section-title">General comments</h3>
          <div class="dm-report__general-comments">{{ generalCommentText }}</div>
        </template>
        <div class="dm-report__page-footer">Presentation</div>
      </section>

      <!-- ============ Matrix slices for the TOC axis — landscape ============ -->
      <section class="dm-report__page dm-report__page--landscape">
        <h2 id="dm-sec-toc" class="dm-report__page-title">Matrix slices for Typology of Object to be Compared (TOC) Axis</h2>
        <p class="dm-report__lede">
          Each Typology of Object to be Compared (e.g. a scenario) is a "slice" of the
          deliberation cube: the Quality or Performance Issue (QPI) crossed with the Stakeholders.
        </p>

        <div v-for="toc in tocs" :key="toc.id" class="dm-report__slice">
          <h3 :id="'dm-toc-' + toc.id" class="dm-report__section-title" :style="tintStyle(toc.moderation_class)">{{ toc.title }}</h3>
          <p v-if="axisDescription(toc)" class="dm-report__axis-desc">{{ axisDescription(toc) }}</p>
          <div v-if="includeComments && sliceCommentText('toc', toc.id)" class="dm-report__slice-discussion">
            <h4 class="dm-report__comments-title">Slice discussion</h4>
            <div class="dm-report__comment-text">{{ sliceCommentText('toc', toc.id) }}</div>
          </div>

          <table class="dm-report__grid">
            <thead>
              <tr>
                <th class="dm-report__grid-corner">Quality or Performance Issue \ Stakeholder</th>
                <th v-for="sh in stakeholders" :key="sh.id">{{ sh.title }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="qpi in qpis" :key="qpi.id">
                <th class="dm-report__grid-rowhead">{{ qpi.title }}</th>
                <td v-for="sh in stakeholders" :key="sh.id" class="dm-report__grid-cell">
                  <template v-for="sum in [cellSummary(sh.id, qpi.id, toc.id)]" :key="sh.id">
                    <div v-if="sum" class="dm-report__cellbar" :style="barStyle(sum)" :title="cellTitle(sh.id, qpi.id, toc.id)">
                      <span class="dm-report__cellbar-label">{{ sum.s }}</span>
                      <span v-if="sum.showPct" class="dm-report__cellbar-pct">{{ sum.pct }}%</span>
                    </div>
                    <span v-else class="dm-report__empty-cell">—</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Per-cell COMMENTS grid: same QPI×Stakeholder layout as above, cells
               hold the comment threads instead of the judgment (only when
               "Include comments" is on and this slice has comments). -->
          <template v-if="includeComments && sliceHasComments(toc.id)">
            <h4 class="dm-report__comments-title">Comments</h4>
            <!-- ≤5 stakeholders: one combined grid mirroring the judgment grid. -->
            <table v-if="!commentGridSplit" class="dm-report__grid dm-report__grid--comments">
              <thead>
                <tr>
                  <th class="dm-report__grid-corner">Quality or Performance Issue \ Stakeholder</th>
                  <th v-for="sh in stakeholders" :key="sh.id">{{ sh.title }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="qpi in qpis" :key="qpi.id">
                  <th class="dm-report__grid-rowhead">{{ qpi.title }}</th>
                  <td v-for="sh in stakeholders" :key="sh.id" class="dm-report__grid-cell dm-report__comment-cell">
                    <span v-if="cellCommentText(sh.id, qpi.id, toc.id)" class="dm-report__comment-text">{{ cellCommentText(sh.id, qpi.id, toc.id) }}</span>
                    <span v-else class="dm-report__empty-cell">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <!-- >5 stakeholders: one table per stakeholder (columns too narrow combined). -->
            <template v-else>
              <div v-for="sh in stakeholders" :key="sh.id" class="dm-report__comment-sh-block">
                <h5 class="dm-report__comments-sh">{{ sh.title }}</h5>
                <table class="dm-report__grid dm-report__grid--comments">
                  <tbody>
                    <tr v-for="qpi in qpis" :key="qpi.id">
                      <th class="dm-report__grid-rowhead">{{ qpi.title }}</th>
                      <td class="dm-report__grid-cell dm-report__comment-cell">
                        <span v-if="cellCommentText(sh.id, qpi.id, toc.id)" class="dm-report__comment-text">{{ cellCommentText(sh.id, qpi.id, toc.id) }}</span>
                        <span v-else class="dm-report__empty-cell">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>

          <div class="dm-report__analysis-space"><strong>Analysis of this slice</strong> — blank space in the exported document.</div>
        </div>
        <div class="dm-report__page-footer">TOC slices</div>
      </section>

      <!-- ============ Matrix slices for the QPI axis — landscape ============ -->
      <section class="dm-report__page dm-report__page--landscape">
        <h2 id="dm-sec-qpi" class="dm-report__page-title">Matrix slices for Quality or Performance Issue (QPI) Axis</h2>
        <p class="dm-report__lede">
          Each Quality or Performance Issue is a "slice" of the deliberation cube: the
          Typology of Object to be Compared (TOC) crossed with the Stakeholders.
        </p>

        <div v-for="qpi in qpis" :key="qpi.id" class="dm-report__slice">
          <h3 :id="'dm-qpi-' + qpi.id" class="dm-report__section-title" :style="tintStyle(qpi.moderation_class)">{{ qpi.title }}</h3>
          <p v-if="axisDescription(qpi)" class="dm-report__axis-desc">{{ axisDescription(qpi) }}</p>
          <div v-if="includeComments && sliceCommentText('qpi', qpi.id)" class="dm-report__slice-discussion">
            <h4 class="dm-report__comments-title">Slice discussion</h4>
            <div class="dm-report__comment-text">{{ sliceCommentText('qpi', qpi.id) }}</div>
          </div>

          <table class="dm-report__grid">
            <thead>
              <tr>
                <th class="dm-report__grid-corner">Typology of Object to be Compared \ Stakeholder</th>
                <th v-for="sh in stakeholders" :key="sh.id">{{ sh.title }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="toc in tocs" :key="toc.id">
                <th class="dm-report__grid-rowhead">{{ toc.title }}</th>
                <td v-for="sh in stakeholders" :key="sh.id" class="dm-report__grid-cell">
                  <template v-for="sum in [cellSummary(sh.id, qpi.id, toc.id)]" :key="sh.id">
                    <div v-if="sum" class="dm-report__cellbar" :style="barStyle(sum)" :title="cellTitle(sh.id, qpi.id, toc.id)">
                      <span class="dm-report__cellbar-label">{{ sum.s }}</span>
                      <span v-if="sum.showPct" class="dm-report__cellbar-pct">{{ sum.pct }}%</span>
                    </div>
                    <span v-else class="dm-report__empty-cell">—</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="dm-report__analysis-space"><strong>Analysis of this slice</strong> — blank space in the exported document.</div>
        </div>
        <div class="dm-report__page-footer">QPI slices</div>
      </section>

      <!-- ============ Matrix slices for the Stakeholder axis — landscape ============ -->
      <section class="dm-report__page dm-report__page--landscape">
        <h2 id="dm-sec-sh" class="dm-report__page-title">Matrix slices for Stakeholder Axis</h2>
        <p class="dm-report__lede">
          Each Stakeholder is a "slice" of the deliberation cube: the Quality or Performance
          Issue (QPI) crossed with the Typology of Object to be Compared (TOC).
        </p>

        <div v-for="sh in stakeholders" :key="sh.id" class="dm-report__slice">
          <h3 :id="'dm-sh-' + sh.id" class="dm-report__section-title" :style="tintStyle(sh.moderation_class)">{{ sh.title }}</h3>
          <p v-if="axisDescription(sh)" class="dm-report__axis-desc">{{ axisDescription(sh) }}</p>
          <div v-if="includeComments && sliceCommentText('stakeholder', sh.id)" class="dm-report__slice-discussion">
            <h4 class="dm-report__comments-title">Slice discussion</h4>
            <div class="dm-report__comment-text">{{ sliceCommentText('stakeholder', sh.id) }}</div>
          </div>

          <table class="dm-report__grid">
            <thead>
              <tr>
                <th class="dm-report__grid-corner">Quality or Performance Issue \ Object (TOC)</th>
                <th v-for="toc in tocs" :key="toc.id">{{ toc.title }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="qpi in qpis" :key="qpi.id">
                <th class="dm-report__grid-rowhead">{{ qpi.title }}</th>
                <td v-for="toc in tocs" :key="toc.id" class="dm-report__grid-cell">
                  <template v-for="sum in [cellSummary(sh.id, qpi.id, toc.id)]" :key="sh.id">
                    <div v-if="sum" class="dm-report__cellbar" :style="barStyle(sum)" :title="cellTitle(sh.id, qpi.id, toc.id)">
                      <span class="dm-report__cellbar-label">{{ sum.s }}</span>
                      <span v-if="sum.showPct" class="dm-report__cellbar-pct">{{ sum.pct }}%</span>
                    </div>
                    <span v-else class="dm-report__empty-cell">—</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="dm-report__analysis-space"><strong>Analysis of this slice</strong> — blank space in the exported document.</div>
        </div>
        <div class="dm-report__page-footer">Stakeholder slices</div>
      </section>

      <!-- ============ Knot View — landscape ============ -->
      <section class="dm-report__page dm-report__page--landscape">
        <h2 id="dm-sec-knot" class="dm-report__page-title">Knot View — indicator mobilization</h2>
        <p class="dm-report__lede">
          Every mobilized indicator across the 3D deliberation structure: acronym/label,
          judgment and weight, Object (TOC), Quality or Performance Issue (QPI), Stakeholder
          and comments.
        </p>
        <p class="dm-report__sort-note">{{ KNOT_SORT_NOTE }}</p>

        <table class="dm-report__table dm-report__table--knot">
          <thead>
            <tr>
              <th>Indicator</th>
              <th>Judgment</th>
              <th>Weight</th>
              <th>Typology of Object to be Compared</th>
              <th>Quality or Performance Issue</th>
              <th>Stakeholder</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sortedKnotRows" :key="row.indicatorCoords">
              <td>
                <a v-if="row.iprHref" :href="row.iprHref" :class="{ 'dm-report__orphan': isOrphanIpr(row.iprId) }" :style="tintStyle(getIndicatorInfo(row.iprId).moderation_class)">{{ row.iprTitle }}</a>
                <span v-else :style="tintStyle(getIndicatorInfo(row.iprId).moderation_class)">{{ row.iprTitle }}</span>
              </td>
              <td>
                <span class="dm-report__chip" :style="{ backgroundColor: row.evaluationColor, color: readableTextColor(row.evaluationColor) }">
                  {{ row.evaluationLabel }}
                </span>
              </td>
              <td>{{ row.sliderValue }}%</td>
              <td>{{ row.tocTitle }}</td>
              <td>{{ row.qpiTitle }}</td>
              <td>{{ row.stakeholderTitle }}</td>
              <td class="dm-report__comment-count">
                <span v-if="includeComments && commentText(row.indicatorCoords)" class="dm-report__comment-text">{{ commentText(row.indicatorCoords) }}</span><span v-else-if="row.commentCount > 0"><i class="bi bi-chat-dots me-1"></i>{{ row.commentCount }}</span>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
            <tr v-if="knotRows.length === 0">
              <td colspan="7" class="dm-report__empty-row">No evaluations recorded.</td>
            </tr>
          </tbody>
        </table>
        <div class="dm-report__page-footer">Knot View</div>
      </section>

      <!-- ============ Axis values — full fields (portrait) — "All fields" ====
           A chapter listing every axis value + IPR with its FULL field set
           (fetched on demand). Only when the "All fields" toggle is on. -->
      <section v-if="allFields && allFieldsEntries.length" class="dm-report__page dm-report__page--portrait">
        <h2 id="dm-sec-axis-values" class="dm-report__page-title">Axis values — full fields</h2>
        <div v-for="entry in allFieldsEntries" :key="entry.group + '-' + entry.id" class="dm-report__axisfull">
          <h3 class="dm-report__section-title">{{ entry.title }}</h3>
          <div class="dm-report__axisfull-group">{{ entry.group }}</div>
          <div class="dm-report__axisfull-body" v-html="entry.html"></div>
        </div>
        <div class="dm-report__page-footer">Axis values</div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.dm-report {
  background: #f1f3f5;
}

/* Screen toolbar — light, matching the shared drawer head (.epb-pt-drawer-head)
   so the Matrix report is consistent with the Collection / Manage / Today
   drawers (same 90% drawer chrome). */
.dm-report__toolbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #fff;
  color: #2c3e50;
  border-bottom: 1px solid #dee2e6;
}

.dm-report__toolbar-title {
  font-weight: 600;
  font-size: 1rem;
}

.dm-report__toolbar-actions {
  display: flex;
  gap: 0.5rem;
}

.dm-report__pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
}

/* Each page is a sheet on screen. Portrait by default; landscape variant wider. */
.dm-report__page {
  position: relative;
  box-sizing: border-box;
  padding: 18mm 16mm 22mm;
  background: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
  color: #212529;
}

.dm-report__page--portrait {
  width: 210mm;
  min-height: 297mm;
}

.dm-report__page--landscape {
  width: 297mm;
  min-height: 210mm;
}

.dm-report__page-footer {
  position: absolute;
  bottom: 10mm;
  right: 16mm;
  font-size: 0.75rem;
  color: #888;
}

/* Cover page */
.dm-report__page--cover {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dm-report__cover-inner {
  text-align: center;
}

.dm-report__cover-logo {
  max-height: 90px;
  max-width: 220px;
  object-fit: contain;
  margin-bottom: 2rem;
}

.dm-report__cover-group {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 3rem;
}

.dm-report__cover-kicker {
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 1rem;
}

.dm-report__cover-title {
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 2.5rem;
  color: #212529;
}

.dm-report__cover-date {
  font-size: 0.95rem;
  color: #666;
}

/* Workflow-state legend on the cover — discreet single inline line. */
.dm-report__legend {
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.3rem 1rem;
  font-size: 0.75rem;
  color: #555;
}

.dm-report__legend-title {
  font-weight: 600;
  color: #34495e;
}

.dm-report__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.dm-report__legend-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

/* Section typography */
.dm-report__page-title {
  font-size: 1.5rem;
  font-weight: 700;
  border-bottom: 2px solid #2c3e50;
  padding-bottom: 0.4rem;
  margin: 0 0 1.25rem;
  color: #212529;
}

.dm-report__section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 1.5rem 0 0.5rem;
  color: #2c3e50;
}

.dm-report__subsection-title {
  font-size: 0.98rem;
  font-weight: 600;
  margin: 1rem 0 0.4rem;
  color: #495057;
}

.dm-report__lede {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 1rem;
}

/* Axis value description (intro under a section title). */
.dm-report__axis-desc {
  font-size: 0.85rem;
  color: #343a40;
  margin: 0 0 0.75rem;
  line-height: 1.45;
}

/* Table of contents */
.dm-report__toc {
  font-size: 1rem;
  line-height: 1.8;
  color: #212529;
  padding-left: 1.4rem;
}

.dm-report__toc-note {
  margin-top: 1.5rem;
  font-size: 0.82rem;
  color: #6c757d;
  font-style: italic;
}

/* Nested slice titles under each axis in the contents preview. */
.dm-report__toc-sub {
  list-style: disc;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #495057;
  margin: 0.2rem 0 0.4rem 0;
  padding-left: 1.4rem;
}

/* The Knot View sort-keys caption, printed above its table. */
.dm-report__sort-note {
  font-size: 0.82rem;
  color: #6c757d;
  font-style: italic;
  margin: -0.5rem 0 1rem;
}

/* Axis description definition lists. */
.dm-report__desc-list {
  margin: 0 0 1rem;
  font-size: 0.85rem;
}

.dm-report__desc-list dt {
  font-weight: 600;
  color: #212529;
  margin-top: 0.5rem;
}

.dm-report__desc-list dd {
  margin: 0 0 0.2rem;
  color: #343a40;
  line-height: 1.45;
}

.dm-report__desc-empty {
  color: #adb5bd;
  font-style: italic;
}

/* Metadata block */
.dm-report__meta {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.dm-report__meta-row {
  display: flex;
  border-bottom: 1px solid #eee;
}

.dm-report__meta-row:last-child {
  border-bottom: none;
}

.dm-report__meta-label {
  flex: 0 0 35%;
  padding: 0.6rem 0.9rem;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 0.85rem;
  color: #212529;
}

.dm-report__meta-value {
  flex: 1;
  padding: 0.6rem 0.9rem;
  font-size: 0.9rem;
  word-break: break-word;
  color: #212529;
}

.dm-report__meta-link {
  color: #1565c0;
}
/* Essential dependencies (Worksite/KIK/KRR) under the Matrix type. */
.dm-report__deps {
  list-style: disc;
  margin: 0.3rem 0 0;
  padding-left: 1.2rem;
  font-size: 0.85rem;
}
.dm-report__deps li {
  margin: 0.1rem 0;
}
/* "All fields" — Axis values chapter. */
.dm-report__axisfull {
  margin-bottom: 1.25rem;
  page-break-inside: avoid;
}
.dm-report__axisfull-group {
  font-size: 0.72rem;
  color: #6c757d;
  margin: 0 0 0.35rem;
}
.dm-report__axisfull-body {
  font-size: 0.85rem;
  color: #343a40;
}

/* Slice grid + tables */
.dm-report__slice {
  margin-bottom: 2rem;
  page-break-inside: avoid;
}

.dm-report__grid,
.dm-report__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  /* Fit the page width regardless of column count: equal-share columns whose
     content wraps, so a wide Enjeu×Stakeholder grid never overflows the (A4
     landscape) sheet. */
  table-layout: fixed;
}

/* The slice grid (Enjeu × Stakeholder) shares the same fit-to-page behaviour. */
.dm-report__grid {
  width: 100%;
  table-layout: fixed;
}

/* Long headers / labels (e.g. "Group5_ALPAB_SGG_Hydroeng.") must wrap, not
   stretch the column past the page. */
.dm-report__grid th,
.dm-report__grid td,
.dm-report__table th,
.dm-report__table td {
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* fix #4: body cells and row-headers use dark text on light backgrounds. */
.dm-report__grid th,
.dm-report__grid td {
  border: 1px solid #ced4da;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  text-align: left;
  color: #212529;
}

.dm-report__grid thead th {
  background: #495057;
  color: #fff;
  font-weight: 600;
}

.dm-report__grid-corner {
  background: #343a40 !important;
  color: #fff !important;
}

.dm-report__grid-rowhead {
  background: #f1f3f5;
  color: #212529;
  font-weight: 600;
}

.dm-report__grid-cell {
  min-width: 70px;
  color: #212529;
  padding: 2px !important;
}

/* Aggregated cell: a proportional bar (winner colour fills pct%) mirroring the
   matrix SummaryView, with the winning judgment label + (optional) % number. */
.dm-report__cellbar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 3px 4px;
  border-radius: 3px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.dm-report__cellbar-label {
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  line-height: 1.15;
}
.dm-report__cellbar-pct {
  font-size: 10px;
  color: #333;
}

.dm-report__empty-cell {
  color: #adb5bd;
}

/* Data tables */
.dm-report__table th,
.dm-report__table td {
  border-bottom: 1px solid #e9ecef;
  padding: 0.4rem 0.55rem;
  text-align: left;
  vertical-align: top;
  color: #212529;
}

.dm-report__table thead th {
  background: #495057;
  color: #fff;
  font-weight: 600;
}

.dm-report__table--knot {
  font-size: 0.72rem;
}

.dm-report__crossing {
  margin-bottom: 1.5rem;
  page-break-inside: avoid;
}

.dm-report__chip {
  display: inline-block;
  padding: 0.12rem 0.45rem;
  margin: 1px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 500;
  white-space: nowrap;
}

.dm-report__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  /* The toolbar background is white, so the label must be dark to be legible. */
  color: #2c3e50;
  cursor: pointer;
  margin-right: 0.25rem;
  white-space: nowrap;
}
.dm-report__branding-note {
  flex: 0 0 100%;
  width: 100%;
  color: #664d03;
  background: #fff8e6;
  border: 1px solid #ffe69c;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
}
.dm-report__branding-note-title {
  color: #664d03;
  font-weight: 600;
}
.dm-report__branding-note-body {
  margin-top: 0.35rem;
  line-height: 1.4;
}
.dm-report__branding-note-body ul {
  padding-left: 1.1rem;
  margin-top: 0.25rem;
}
.dm-report__comment-text {
  white-space: pre-line;
  font-size: 0.7rem;
  color: #343a40;
}
/* General (matrix-level) comments at the end of the Presentation section. */
.dm-report__general-comments {
  white-space: pre-line;
  font-size: 0.85rem;
  color: #343a40;
  border-left: 3px solid #ced4da;
  background: #f8f9fa;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0 1rem;
}
/* Slice-level discussion thread, shown under each slice's heading. */
.dm-report__slice-discussion {
  border-left: 3px solid #ced4da;
  background: #f8f9fa;
  padding: 0.4rem 0.75rem;
  margin: 0.4rem 0 0.75rem;
}
.dm-report__slice-discussion .dm-report__comments-title {
  margin: 0 0 0.25rem;
}
.dm-report__slice-discussion .dm-report__comment-text {
  font-size: 0.8rem;
}
/* Per-cell comment grids under each TOC slice. */
.dm-report__comments-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #34495e;
  margin: 0.75rem 0 0.35rem;
}
.dm-report__comments-sh {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin: 0.6rem 0 0.2rem;
}
.dm-report__comment-sh-block {
  margin-bottom: 0.5rem;
}
.dm-report__grid--comments .dm-report__comment-cell {
  text-align: left;
  vertical-align: top;
}
.dm-report__analysis-space {
  border-left: 3px solid #ced4da;
  background: #f8f9fa;
  padding: 0.5rem 0.75rem;
  margin: 0.75rem 0;
  color: #495057;
  font-size: 0.85rem;
}
.dm-report__comment-count {
  white-space: nowrap;
  color: #1976d2;
}

.dm-report__empty-row {
  text-align: center;
  font-style: italic;
  color: #adb5bd;
  padding: 0.75rem;
}

.dm-report__orphan {
  font-weight: 700;
}

.dm-report__table a,
.dm-report__grid a {
  color: #1565c0;
  text-decoration: none;
}

/* ===================== PRINT (A4, one section per page) ===================== */
/* fix #1: named pages — portrait sheets vs. landscape matrix-table sheets. */
@page portrait {
  size: A4 portrait;
  margin: 14mm;
}

@page landscape {
  size: A4 landscape;
  margin: 12mm;
}

@media print {
  .dm-report,
  .dm-report__pages {
    background: #fff !important;
    padding: 0 !important;
    gap: 0 !important;
  }

  .dm-report__toolbar {
    display: none !important;
  }

  .dm-report__page {
    width: auto;
    min-height: auto;
    box-shadow: none;
    margin: 0;
    padding: 0;
    /* Hard page break between sections. */
    page-break-after: always;
    break-after: page;
  }

  /* Bind each page element to its named @page rule so orientation follows. */
  .dm-report__page--portrait {
    page: portrait;
  }

  .dm-report__page--landscape {
    page: landscape;
  }

  .dm-report__page:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  .dm-report__page-footer {
    bottom: 0;
    right: 0;
  }

  /* Force the judgment colours and dark headers to render. */
  .dm-report__chip,
  .dm-report__legend-swatch,
  .dm-report__grid thead th,
  .dm-report__grid-corner,
  .dm-report__grid-rowhead,
  .dm-report__table thead th {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
